import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui'
import type { Book } from '@/types'

interface RouletteResultProps {
  selectedBook: Book
  onMarkAsNextRead?: () => void
  onViewHistory?: () => void
}

export function RouletteResult({
  selectedBook,
  onMarkAsNextRead,
  onViewHistory,
}: RouletteResultProps) {
  const { t } = useTranslation()
  const [showConfetti, setShowConfetti] = useState(true)
  const [isRevealed, setIsRevealed] = useState(false)

  useEffect(() => {
    // Delay the reveal for dramatic effect
    const revealTimeout = setTimeout(() => setIsRevealed(true), 500)

    // Hide confetti after animation
    const confettiTimeout = setTimeout(() => setShowConfetti(false), 3000)

    return () => {
      clearTimeout(revealTimeout)
      clearTimeout(confettiTimeout)
    }
  }, [])

  return (
    <div className="relative h-screen flex items-center justify-center p-4 overflow-hidden">
      {/* Background Gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-primary/5 to-primary/10 -z-10"></div>

      {/* Result Container */}
      <div
        className={`
        max-w-lg mx-auto text-center space-y-4 transition-all duration-1000 transform
        ${isRevealed ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}
      `}
      >
        {/* Winner Announcement */}
        <div className="space-y-3">
          <div className="flex items-center justify-center gap-4">
            {showConfetti && <div className="text-4xl animate-bounce">🎉</div>}

            <h1 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-primary via-primary to-primary-dark animate-pulse">
              {t('roulette.winner')}
            </h1>

            {showConfetti && (
              <div className="text-4xl animate-bounce" style={{ animationDelay: '0.3s' }}>
                🎊
              </div>
            )}
          </div>

          <div className="text-lg text-muted-foreground font-medium">
            {t('roulette.yourNextRead')}
          </div>
        </div>

        {/* Selected Book Display */}
        <div
          className={`
          transform transition-all duration-700 delay-300
          ${isRevealed ? 'scale-100 rotate-0' : 'scale-75 rotate-12'}
        `}
        >
          <div className="relative inline-block">
            {/* Glow effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-primary to-primary-dark rounded-xl blur-lg opacity-50 animate-pulse"></div>

            {/* Custom Book Card with Fixed Size */}
            <div className="relative bg-card rounded-xl p-3 shadow-lg border border-border hover:shadow-xl transition-shadow w-72">
              <div className="text-center">
                {/* Book Cover */}
                <div className="w-28 h-40 mx-auto mb-3 bg-gradient-to-br from-muted/50 to-muted rounded-lg overflow-hidden shadow-sm">
                  {selectedBook.cover_url ? (
                    <img
                      src={selectedBook.cover_url}
                      alt={selectedBook.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <div className="text-center p-3">
                        <div className="text-muted-foreground mb-2">
                          <svg
                            className="w-10 h-10 mx-auto"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={1.5}
                              d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25"
                            />
                          </svg>
                        </div>
                        <p className="text-xs font-medium text-muted-foreground line-clamp-2">
                          {selectedBook.title}
                        </p>
                        <p className="text-xs text-muted-foreground/70 mt-1">
                          {selectedBook.author}
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Book Title and Author */}
                <div className="space-y-1">
                  <h3 className="font-semibold text-base text-foreground line-clamp-2">
                    {selectedBook.title}
                  </h3>
                  <p className="text-sm text-muted-foreground">{selectedBook.author}</p>

                  {/* Status Badge */}
                  <div className="flex justify-center mt-2">
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary border border-primary/20">
                      {t(`books.status.${selectedBook.status}`)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Winner badge */}
              <div className="absolute -top-2 -right-2 bg-gradient-to-r from-amber to-warning text-primary-foreground px-3 py-1 rounded-full font-bold text-xs shadow-lg animate-bounce">
                🏆 {t('roulette.winner')}
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div
          className={`
          flex flex-col sm:flex-row gap-3 justify-center transition-all duration-700 delay-500
          ${isRevealed ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'}
        `}
        >
          {onMarkAsNextRead && (
            <Button
              onClick={onMarkAsNextRead}
              className="bg-success text-primary-foreground hover:bg-success/90 px-8 py-3 text-lg font-semibold shadow-lg transform hover:scale-105 transition-all"
            >
              📖 {t('roulette.startReadingNow')}
            </Button>
          )}

          {onViewHistory && (
            <Button
              onClick={onViewHistory}
              variant="outline"
              className="border-2 border-primary text-primary hover:bg-primary/5 px-8 py-3 text-lg font-semibold shadow-lg transform hover:scale-105 transition-all"
            >
              📜 {t('roulette.viewHistory')}
            </Button>
          )}
        </div>

        {/* Fun Fact */}
        <div
          className={`
          text-sm text-muted-foreground transition-all duration-700 delay-700
          ${isRevealed ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'}
        `}
        >
          <p>✨ {t('roulette.funFact')}</p>
        </div>
      </div>
    </div>
  )
}
